import { LinearProgress } from "@mui/material";
import { LngLat } from "mapbox-gl";
import { observer } from "mobx-react-lite";
import pLimit from "p-limit";
import { useRef, useState } from "react";
import { BaseRow, Importer, ImporterField } from "react-csv-importer";
import "react-csv-importer/dist/index.css";
import usePropertySearch from "../../hooks/usePropertySearch";
import { useLocationStore } from "../../index";
import IProperty from "../../interfaces/IProperty";
import commonStyles from "../Common.module.css";
import addLocationsStyles from "./AddLocations.module.css";
import "./Importer.css";
import MultipleLocationsDetails from "./MultipleLocationsDetails";

function UploadMultipleLocations({ companyId, onUploadStarted, onUploadCanceled, onClose }: IUploadMultipleLocationsProps) {
    const locationStore = useLocationStore();
    const { searchByAddress, searchByLngLat } = usePropertySearch();

    const [isImportStarted, setIsImportStarted] = useState(false);
    const [isImportCompleted, setIsImportCompleted] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processedCount, setProcessedCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [currentAction, setCurrentAction] = useState<string>("");

    const cancelRef = useRef(false);
    const rawRowsRef = useRef<BaseRow[]>([]);
    const locationInfosRef = useRef<IUploadedLocationInfo[]>([]);
    const uniqueKeysRef = useRef<Set<string>>(new Set());

    const handleStart = () => {
        setIsImportStarted(true);
        onUploadStarted();
    };

    const handleCancelProcessing = () => {
        cancelRef.current = true;
        setIsProcessing(false);
        onClose();
    };

    const handleData = (rows: BaseRow[]) => {
        rawRowsRef.current.push(...rows);
    };

    const processRow = async (row: BaseRow, index: number) => {
        if (cancelRef.current) return;

        const siteName = (row.siteName as string) || "";
        const address = (row.address as string) || "";
        const lat = (row.lat as number) || null;
        const lon = (row.lon as number) || null;
        const key = `${companyId}-${address}-${lat},${lon}`;

        const info: IUploadedLocationInfo = {
            serialNumber: index + 1,
            siteName,
            address,
            lat,
            lon,
            notes: (row.notes as string) || "",
            status: UploadedLocationStatus.CanBeAdded,
        };

        if (uniqueKeysRef.current.has(key)) {
            info.status = UploadedLocationStatus.Duplicate;
        } else {
            uniqueKeysRef.current.add(key);
        }
        if (!address && !(lat && lon)) {
            info.status = UploadedLocationStatus.AddressOrLatLonIsRequired;
        }

        const actionName = address || (lat && lon ? `${lat},${lon}` : "location");
        setCurrentAction(`Geocoding ${actionName}`);

        if (info.status === UploadedLocationStatus.CanBeAdded && !cancelRef.current) {
            const property = lat && lon
                ? await searchByLngLat(new LngLat(lon, lat))
                : await searchByAddress(address);

            if (property) {
                info.property = property;
                info.siteName ||= `${property.city}, ${property.state}`;
                info.address ||= property.address;
            } else {
                info.status = UploadedLocationStatus.PropertyNotFound;
            }

            if (
                info.status === UploadedLocationStatus.CanBeAdded &&
                locationStore.locations.some(l => l.pipedriveCompanyId === companyId && (l.address === info.address || l.regridId === info.property?.regridId))
            ) {
                info.status = UploadedLocationStatus.AlreadyExist;
            }
        }

        locationInfosRef.current.push(info);
        setProcessedCount(c => c + 1);
    };

    const handleComplete = async () => {
        setIsImportStarted(false);
        const total = rawRowsRef.current.length;
        setTotalCount(total);
        setIsProcessing(true);

        const limit = pLimit(5);
        const tasks: Promise<void>[] = [];
        for (let idx = 0; idx < rawRowsRef.current.length; idx++) {
            if (cancelRef.current) break;
            tasks.push(limit(() => processRow(rawRowsRef.current[idx], idx)));
        }

        await Promise.all(tasks);

        if (!cancelRef.current) {
            setIsProcessing(false);
            setIsImportCompleted(true);
        }
    };

    return (
        <>
            {(isImportStarted || isImportCompleted) &&
                <div className={addLocationsStyles.detailsTitle}>Upload Multiple Locations</div>
            }
            {!isImportCompleted ? (
                <div>
                    <Importer
                        dataHandler={handleData}
                        onStart={handleStart}
                        onComplete={handleComplete}
                    >
                        <ImporterField name="siteName" label="Site Name" optional />
                        <ImporterField name="address" label="Address" optional />
                        <ImporterField name="lat" label="Lat" optional />
                        <ImporterField name="lon" label="Lon" optional />
                        <ImporterField name="notes" label="Notes" optional />
                    </Importer>
                    {isProcessing && (
                        <div className={addLocationsStyles.customProgressWrapper}>
                            <div className={addLocationsStyles.customProgressTitle}>{currentAction}</div>
                            <LinearProgress
                                color="inherit"
                                variant="determinate"
                                value={(processedCount / totalCount) * 100}
                                className={addLocationsStyles.customProgress}
                            />
                            <div className={addLocationsStyles.customProgressCount}>
                                {processedCount} of {totalCount}
                            </div>
                        </div>
                    )}

                    <div className={[commonStyles.modalControls, commonStyles.controlGroup].join(" ")}>
                        <button
                            className={commonStyles.secondaryButton}
                            onClick={isProcessing ? handleCancelProcessing : onClose}>
                            Cancel
                        </button>
                    </div>
                </div>
            ) : (
                <MultipleLocationsDetails
                    companyId={companyId}
                    locationsInfo={locationInfosRef.current}
                    onImportCanceled={() => {
                        setIsImportCompleted(false);
                        onUploadCanceled();
                    }}
                    onAdd={onClose}
                />
            )}
        </>
    );
}

interface IUploadMultipleLocationsProps {
    companyId: number;
    onUploadStarted: () => void;
    onUploadCanceled: () => void;
    onClose: () => void;
}

export interface IUploadedLocationInfo {
    serialNumber: number;
    siteName: string;
    address: string;
    lat: number | null;
    lon: number | null;
    notes: string;
    property?: IProperty;
    status: UploadedLocationStatus;
}

export enum UploadedLocationStatus {
    CanBeAdded,
    Duplicate,
    PropertyNotFound,
    AlreadyExist,
    AddressOrLatLonIsRequired,
}

export default observer(UploadMultipleLocations);